import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Truck, User, Lock, CheckCircle2, AlertCircle, Save, Eye, EyeOff, Percent } from 'lucide-react';
import { settingsService, authService } from '../services/api.service';

const Settings = () => {
  const { user } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('shipping'); // 'shipping' | 'profile' | 'password'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // 1. Shipping & Tax Settings State
  const [shippingConfig, setShippingConfig] = useState({
    standard_shipping_fee: '15.00',
    free_shipping_threshold: '500.00',
    vat_tax_percent: '20.00',
  });

  // 2. Profile Settings State
  const [profileData, setProfileData] = useState({
    name: user?.name || 'InfraEdge Admin',
    email: user?.email || 'Info@infraedgeredlight.co.uk',
  });

  // 3. Password Settings State
  const [passData, setPassData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await settingsService.getSettings();
      if (res.settings) {
        setShippingConfig({
          standard_shipping_fee: res.settings.standard_shipping_fee || res.settings.shipping_rate || '15.00',
          free_shipping_threshold: res.settings.free_shipping_threshold || '500.00',
          vat_tax_percent: res.settings.vat_tax_percent !== undefined && res.settings.vat_tax_percent !== null ? res.settings.vat_tax_percent : '20.00',
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Save Shipping & Tax Settings
  const handleSaveShippingSettings = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMsg('');

      await settingsService.updateSettings({
        standard_shipping_fee: parseFloat(shippingConfig.standard_shipping_fee || 0),
        free_shipping_threshold: parseFloat(shippingConfig.free_shipping_threshold || 0),
        vat_tax_percent: parseFloat(shippingConfig.vat_tax_percent || 0)
      });
      setSuccessMsg('Shipping & VAT tax settings saved successfully!');
    } catch (err) {
      setError(err.message || 'Failed to save shipping & tax settings.');
    } finally {
      setSaving(false);
    }
  };

  // Update Admin Profile
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMsg('');

      const res = await authService.updateProfile(profileData.name, profileData.email);
      setSuccessMsg(res.message || 'Admin profile updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  // Change Admin Password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passData.currentPassword || !passData.newPassword) {
      setError('Please enter both current and new passwords.');
      return;
    }
    if (passData.newPassword !== passData.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (passData.newPassword.length < 4) {
      setError('New password must be at least 4 characters long.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMsg('');

      const res = await authService.changePassword(passData.currentPassword, passData.newPassword);
      setSuccessMsg(res.message || 'Password changed successfully!');
      setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Page Header */}
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>Shipping & Tax Administration</h2>
        <p style={{ fontSize: '13px', color: '#64748b' }}>Configure standard shipping fees, free shipping order threshold, VAT tax percentage & admin security.</p>
      </div>

      {successMsg && (
        <div className="form-alert" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' }}>
          <CheckCircle2 size={16} />
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="form-alert">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* 3 Navigation Tabs */}
      <div style={{ display: 'flex', gap: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
        <button
          onClick={() => { setActiveTab('shipping'); setError(''); setSuccessMsg(''); }}
          style={{
            padding: '10px 18px',
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '13px',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'shipping' ? '#dc2626' : '#f8fafc',
            color: activeTab === 'shipping' ? '#ffffff' : '#64748b',
          }}
        >
          <Truck size={15} style={{ display: 'inline', marginRight: '6px' }} />
          Shipping & Tax Settings
        </button>

        <button
          onClick={() => { setActiveTab('profile'); setError(''); setSuccessMsg(''); }}
          style={{
            padding: '10px 18px',
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '13px',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'profile' ? '#dc2626' : '#f8fafc',
            color: activeTab === 'profile' ? '#ffffff' : '#64748b',
          }}
        >
          <User size={15} style={{ display: 'inline', marginRight: '6px' }} />
          Profile Settings
        </button>

        <button
          onClick={() => { setActiveTab('password'); setError(''); setSuccessMsg(''); }}
          style={{
            padding: '10px 18px',
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '13px',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'password' ? '#dc2626' : '#f8fafc',
            color: activeTab === 'password' ? '#ffffff' : '#64748b',
          }}
        >
          <Lock size={15} style={{ display: 'inline', marginRight: '6px' }} />
          Password Settings
        </button>
      </div>

      {/* Tab 1: Shipping & Tax Settings Form */}
      <div className="table-card-box" style={{ maxWidth: '640px' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading settings...</div>
        ) : activeTab === 'shipping' ? (
          <form onSubmit={handleSaveShippingSettings} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Truck size={22} color="#dc2626" />
              <div>
                <h3 style={{ fontSize: '16.5px', fontWeight: 800, color: '#0f172a', margin: 0 }}>Shipping & VAT Tax Configuration</h3>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '2px 0 0 0' }}>Set shipping fees, free shipping threshold, and VAT tax percentage applied to customer carts.</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '6px' }}>
              <div>
                <label className="input-label">Standard Shipping Fee (£)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="styled-input"
                  placeholder="e.g. 15.00"
                  value={shippingConfig.standard_shipping_fee}
                  onChange={(e) => setShippingConfig({ ...shippingConfig, standard_shipping_fee: e.target.value })}
                  required
                />
                <span style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px', display: 'block' }}>Charged when order total is below free shipping threshold.</span>
              </div>

              <div>
                <label className="input-label">Free Shipping Threshold (£)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="styled-input"
                  placeholder="e.g. 500.00"
                  value={shippingConfig.free_shipping_threshold}
                  onChange={(e) => setShippingConfig({ ...shippingConfig, free_shipping_threshold: e.target.value })}
                  required
                />
                <span style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px', display: 'block' }}>Orders above this total unlock FREE shipping!</span>
              </div>
            </div>

            <div>
              <label className="input-label">VAT Tax Rate (%)</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  className="styled-input"
                  placeholder="e.g. 20.00"
                  value={shippingConfig.vat_tax_percent}
                  onChange={(e) => setShippingConfig({ ...shippingConfig, vat_tax_percent: e.target.value })}
                  required
                  style={{ paddingRight: '36px' }}
                />
                <Percent size={16} color="#94a3b8" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              </div>
              <span style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px', display: 'block' }}>Tax percentage automatically applied on the storefront cart page (e.g. 20% VAT).</span>
            </div>

            <div style={{ marginTop: '10px' }}>
              <button type="submit" className="btn-red-submit" disabled={saving} style={{ width: 'auto', padding: '0 24px', height: '44px' }}>
                <Save size={18} />
                <span>{saving ? 'Saving Settings...' : 'Save Shipping & Tax Settings'}</span>
              </button>
            </div>
          </form>
        ) : activeTab === 'profile' ? (
          /* Tab 2: Profile Settings Form */
          <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Admin Profile Information</h3>

            <div>
              <label className="input-label">Admin Full Name</label>
              <input
                type="text"
                className="styled-input"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="input-label">Admin Email Address</label>
              <input
                type="email"
                className="styled-input"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                required
              />
            </div>

            <div style={{ marginTop: '10px' }}>
              <button type="submit" className="btn-red-submit" disabled={saving} style={{ width: 'auto', padding: '0 24px', height: '44px' }}>
                <User size={18} />
                <span>{saving ? 'Updating Profile...' : 'Update Admin Profile'}</span>
              </button>
            </div>
          </form>
        ) : (
          /* Tab 3: Password Settings Form */
          <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Change Account Password</h3>

            <div className="input-group" style={{ marginBottom: 0 }}>
              <label className="input-label">Current Password</label>
              <div className="input-field-wrapper">
                <Lock className="field-icon" size={18} />
                <input
                  type={showCurrentPass ? 'text' : 'password'}
                  className="styled-input"
                  placeholder="Enter current password"
                  value={passData.currentPassword}
                  onChange={(e) => setPassData({ ...passData, currentPassword: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowCurrentPass(!showCurrentPass)}
                >
                  {showCurrentPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="input-group" style={{ marginBottom: 0 }}>
              <label className="input-label">New Password</label>
              <div className="input-field-wrapper">
                <Lock className="field-icon" size={18} />
                <input
                  type={showNewPass ? 'text' : 'password'}
                  className="styled-input"
                  placeholder="Enter new password"
                  value={passData.newPassword}
                  onChange={(e) => setPassData({ ...passData, newPassword: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowNewPass(!showNewPass)}
                >
                  {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="input-group" style={{ marginBottom: 0 }}>
              <label className="input-label">Confirm New Password</label>
              <div className="input-field-wrapper">
                <Lock className="field-icon" size={18} />
                <input
                  type={showNewPass ? 'text' : 'password'}
                  className="styled-input"
                  placeholder="Confirm new password"
                  value={passData.confirmPassword}
                  onChange={(e) => setPassData({ ...passData, confirmPassword: e.target.value })}
                  required
                />
              </div>
            </div>

            <div style={{ marginTop: '10px' }}>
              <button type="submit" className="btn-red-submit" disabled={saving} style={{ width: 'auto', padding: '0 24px', height: '44px' }}>
                <Lock size={18} />
                <span>{saving ? 'Updating Password...' : 'Change Admin Password'}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Settings;
